import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from './authentication.service';
import { LogCategory, LogCoreService, LoggerService, LogType } from './logger.service';

@Injectable({
  providedIn: 'root'
})
class AuthGuardService {

    constructor(
        private authentication: AuthenticationService, 
        private logger: LoggerService
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreService, LogCoreService.AuthGuard, category);
    }

    // Determines whether or not a user is authorized to active the route
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {

        let retVal = false;
        let message = '';

        // Get the token from local storage
        let token = this.authentication.token;

        // If there's no token in local storage, then we know the user isn't authenticated, so redirect to login page. 
        this.log("canActivate", `${route.url} - token test: !isFound = ${!token.isFound}; isValid = ${token.isValid}; isRefreshable = ${token.isRefreshable};`);
        if (!token.isFound) {
            message = 'User has no authentication token.';
            this.authentication.authenticate(state.url); // use the state URL, which may be more sepcific than the route
            retVal = false;
        }
        else if (token.isValid) {
            message = 'Valid token; user can route through.';
            retVal = true;
        }
        else if (token.isRefreshable) {
            message = 'Expired but refreshable token; allow route and try to refresh token.';
            // This is a finite observable and will complete automatically -- no unsubscription necessary. 
            this.authentication.tryRefreshToken$().subscribe((data) => {
                    this.log("canActivate", "Token refreshed: " + data);
                },
                (error) => {
                    this.log("canActivate", "Token refresh error: " + error);
                },
                () => {
                    this.log("canActivate", "Token refresh completed.");
                },
            ); 
            retVal = true;
        }
        else {
            message = 'Expired and non-refreshable token; redirecting to login.'; 
            this.authentication.authenticate(state.url);
            retVal = false;
        }

        this.log("canActivate", `${route.url}? ${retVal} - ${message}`);

        return retVal; 
    }
}

export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
    return inject(AuthGuardService).canActivate(next, state);
}
